/**
 * The «Колобки» smiley set and the code that turns `:slug:` into a picture.
 *
 * A message carries smileys as codes inside its ordinary text — `привет :wink:`
 * — rather than as a separate rich-content field. That keeps one message model:
 * a smiley survives the chat-list snippet, the push payload and every existing
 * query untouched, and rendering is a parse on the way out. It is also how these
 * packs worked on the forums they came from.
 *
 * Assets: "Kolobok Smiles" by Aiwan (kolobok.us) — third-party artwork, free for
 * non-commercial use only. See design/design_handoff_sticker_picker/README.md
 * before shipping this commercially.
 */
export interface Smiley {
  /** File name without the extension, and the code typed between colons. */
  slug: string;
  /** Russian display name, from the design handoff's NAMES map. */
  label: string;
}

export const SMILEYS: Smiley[] = [
  { slug: 'acute', label: 'остроумно' },
  { slug: 'aggressive', label: 'злюсь' },
  { slug: 'air_kiss', label: 'воздушный поцелуй' },
  { slug: 'angel', label: 'ангел' },
  { slug: 'bad', label: 'плохо' },
  { slug: 'bb', label: 'пока-пока' },
  { slug: 'beach', label: 'пляж' },
  { slug: 'beee', label: 'бее' },
  { slug: 'big_boss', label: 'большой босс' },
  { slug: 'biggrin', label: 'хохот' },
  { slug: 'blum2', label: 'дразнится' },
  { slug: 'blush', label: 'смущение' },
  { slug: 'boast', label: 'похвастаться' },
  { slug: 'bomb', label: 'бомба' },
  { slug: 'boredom', label: 'скука' },
  { slug: 'bye', label: 'до свидания' },
  { slug: 'clapping', label: 'аплодисменты' },
  { slug: 'cray', label: 'плачет' },
  { slug: 'crazy', label: 'безумие' },
  { slug: 'curtsey', label: 'реверанс' },
  { slug: 'dance4', label: 'танцует' },
  { slug: 'dash1', label: 'убегает' },
  { slug: 'dirol', label: 'дироль' },
  { slug: 'drinks', label: 'за встречу' },
  { slug: 'feminist', label: 'феминистка' },
  { slug: 'flirt', label: 'флирт' },
  { slug: 'focus', label: 'внимание' },
  { slug: 'fool', label: 'дурачок' },
  { slug: 'friends', label: 'друзья' },
  { slug: 'gamer4', label: 'геймер' },
  { slug: 'girl_cray2', label: 'девочка плачет' },
  { slug: 'girl_crazy', label: 'девочка в безумии' },
  { slug: 'girl_drink4', label: 'девочка с бокалом' },
  { slug: 'girl_haha', label: 'девочка хохочет' },
  { slug: 'girl_hospital', label: 'девочка болеет' },
  { slug: 'girl_impossible', label: 'это невозможно' },
  { slug: 'girl_in_love', label: 'девочка влюблена' },
  { slug: 'girl_sigh', label: 'девочка вздыхает' },
  { slug: 'give_heart2', label: 'дарит сердце' },
  { slug: 'give_rose', label: 'дарит розу' },
  { slug: 'good', label: 'отлично' },
  { slug: 'heart', label: 'сердце' },
  { slug: 'help', label: 'на помощь' },
  { slug: 'hi', label: 'привет' },
  { slug: 'hunter', label: 'охотник' },
  { slug: 'hysteric', label: 'истерика' },
  { slug: 'i-m_so_happy', label: 'я счастлив' },
  { slug: 'ireful1', label: 'в ярости' },
  { slug: 'king', label: 'король' },
  { slug: 'kiss2', label: 'поцелуй' },
  { slug: 'kiss3', label: 'поцелуйчик' },
  { slug: 'lazy', label: 'лень' },
  { slug: 'lol', label: 'лол' },
  { slug: 'mail1', label: 'письмо' },
  { slug: 'mamba', label: 'мамба' },
  { slug: 'mega_shock', label: 'мега шок' },
  { slug: 'mocking', label: 'насмешка' },
  { slug: 'moil', label: 'вкалывает' },
  { slug: 'music', label: 'музыка' },
  { slug: 'nea', label: 'неа' },
  { slug: 'new_russian', label: 'новый русский' },
  { slug: 'ok', label: 'окей' },
  { slug: 'paint2', label: 'рисует' },
  { slug: 'pardon', label: 'пардон' },
  { slug: 'party2', label: 'вечеринка' },
  { slug: 'pleasantry', label: 'шутка' },
  { slug: 'popcorn1', label: 'попкорн' },
  { slug: 'prankster2', label: 'шалун' },
  { slug: 'preved', label: 'превед' },
  { slug: 'punish', label: 'наказание' },
  { slug: 'rofl', label: 'катается от смеха' },
  { slug: 'sad', label: 'грусть' },
  { slug: 'sarcastic', label: 'сарказм' },
  { slug: 'scare', label: 'испуг' },
  { slug: 'scratch_one-s_head', label: 'чешет затылок' },
  { slug: 'search', label: 'поиск' },
  { slug: 'secret', label: 'секрет' },
  { slug: 'shock', label: 'шок' },
  { slug: 'shout', label: 'кричит' },
  { slug: 'slow', label: 'тормоз' },
  { slug: 'smile', label: 'улыбка' },
  { slug: 'smoke', label: 'курит' },
  { slug: 'sorry2', label: 'прости' },
  { slug: 'spiteful', label: 'злорадство' },
  { slug: 'spruce_up', label: 'наводит красоту' },
  { slug: 'stop', label: 'стоп' },
  { slug: 'tease', label: 'дразнит' },
  { slug: 'tender', label: 'нежность' },
  { slug: 'thank_you2', label: 'спасибо' },
  { slug: 'this', label: 'вот это' },
  { slug: 'training1', label: 'тренировка' },
  { slug: 'unknown', label: 'не знаю' },
  { slug: 'vampire', label: 'вампир' },
  { slug: 'vava', label: 'бо-бо' },
  { slug: 'victory', label: 'победа' },
  { slug: 'wacko2', label: 'чудик' },
  { slug: 'wink', label: 'подмигивает' },
  { slug: 'wizard', label: 'волшебник' },
  { slug: 'yahoo', label: 'ура' },
  { slug: 'yes3', label: 'да' },
  { slug: 'yess', label: 'есть!' },
];

const BY_SLUG = new Map(SMILEYS.map((smiley) => [smiley.slug, smiley]));

/** Served from client/public, so the URL is stable and cacheable. */
export function smileyUrl(slug: string): string {
  return `/smileys/${slug}.gif`;
}

export function findSmiley(slug: string): Smiley | undefined {
  return BY_SLUG.get(slug);
}

export type MessagePart =
  | { type: 'text'; value: string }
  | { type: 'smiley'; slug: string; label: string };

/**
 * Slugs contain letters, digits, `_` and `-` (`scratch_one-s_head`), so the
 * code pattern has to allow all four. Anything that matches the shape but is
 * not a smiley we ship stays literal text — which is what keeps «в 12:30:15»
 * and «:-)» intact.
 */
const CODE = /:([a-zA-Z0-9_-]{1,20}):/g;

/** Splits a stored message body into the runs of text and smileys to render. */
export function parseMessage(body: string): MessagePart[] {
  const parts: MessagePart[] = [];
  let at = 0;

  for (const match of body.matchAll(CODE)) {
    const smiley = findSmiley(match[1]!);
    if (!smiley) continue;
    if (match.index > at) parts.push({ type: 'text', value: body.slice(at, match.index) });
    parts.push({ type: 'smiley', slug: smiley.slug, label: smiley.label });
    at = match.index + match[0].length;
  }

  if (at < body.length) parts.push({ type: 'text', value: body.slice(at) });
  return parts;
}

/**
 * One smiley and nothing else. Sent alone it is the message, so it renders
 * large and without a bubble — the sticker case from the handoff.
 */
export function isSoloSticker(parts: MessagePart[]): boolean {
  const smileys = parts.filter((part) => part.type === 'smiley');
  const text = parts.filter((part) => part.type === 'text');
  return smileys.length === 1 && text.every((part) => part.value.trim() === '');
}

/** Up to six suggestions for `:que` — matched on the slug, as the handoff specifies. */
export function suggestSmileys(query: string, limit = 6): Smiley[] {
  const needle = query.toLowerCase();
  return SMILEYS.filter((smiley) => smiley.slug.toLowerCase().startsWith(needle)).slice(0, limit);
}

/** The picker's own filtering: Russian label first, then the slug. */
export function searchSmileys(query: string): Smiley[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return SMILEYS;
  return SMILEYS.filter(
    (smiley) => smiley.label.toLowerCase().includes(needle) || smiley.slug.includes(needle),
  );
}
